import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

// material-ui
import { Button, Chip, CssBaseline, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';

//components
import AlertInfo from '../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../components/elements/AlertInfoDialog';
import TopBar from '../../components/nav/TopBar';
import SupplyRequest from './dataObjects/SupplyRequest';
import CloseRequestDialog from './components/CloseRequestDialog';

const CLOSED_DAYS = 60;

const SuppliesApp = (): JSX.Element => {

    //set up browser title
    const pageTitle = 'Extranet - Supply Requests';
    document.title = pageTitle;

    const applicationName = 'Supply Requests';

    //set up state for the app
    const [requests, setRequests] = useState<SupplyRequest[]>([]);
    const [isSupplyAdmin, setIsSupplyAdmin] = useState(false);
    const [employeeId, setEmployeeId] = useState(0);
    const [newRequestText, setNewRequestText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [requestToClose, setRequestToClose] = useState<SupplyRequest>();
    const [requestToWithdraw, setRequestToWithdraw] = useState<SupplyRequest>();

    const openRequests = requests.filter(r => r.isOpen);
    const closedRequests = requests.filter(r => !r.isOpen);

    //payload
    useEffect(() => {
        getPayload();
    }, []);

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
        setRequestToWithdraw(undefined);
    }

    const showAlert = (type: string, title: string, message: string, secondaryMessage?: string, buttonLabel?: string) => {
        setAlertInfo({
            type: type,
            title: title,
            buttonLabel: buttonLabel,
            errorMessage: message,
            secondaryErrorMessage: secondaryMessage ?? ''
        });
        setAlertInfoDialog(true);
    }

    /** Shows an error or warning from an Outcome; returns true when the save went through. */
    const handleOutcome = (result: any, title: string): boolean => {
        if (result.isError) {
            showAlert('error', 'Error: ' + title, result.errorMessage, result.secondaryErrorMessage);
            return false;
        }
        if (result.warningMessage) {
            showAlert('error', title, result.warningMessage);
        }
        return true;
    }

    const getPayload = async () => {
        await fetch(`/Supplies/GetPayload?closedDays=${CLOSED_DAYS}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    showAlert('error', 'Error: Supply Requests', result.errorMessage);
                    return;
                }
                setRequests(result.requests);
                setIsSupplyAdmin(result.isSupplyAdmin);
                setEmployeeId(result.employeeId);
            })
    }

    const post = (url: string, body: object) => {
        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).then(result => {
            if (!result.ok) {
                return result.text().then(text => ({ isError: true, errorMessage: text || result.statusText }));
            }
            return result.json();
        });
    }

    const addRequest = () => {
        if (newRequestText.trim() === '')
            return;

        setIsSaving(true);
        post('/Supplies/AddRequest', { requestText: newRequestText })
            .then(result => {
                if (handleOutcome(result, 'Add Request')) {
                    setNewRequestText('');
                    getPayload();
                }
            })
            .finally(() => setIsSaving(false));
    }

    const updateRequest = (request: SupplyRequest, dateOrdered: string | null, isClosed: boolean, adminNote: string | null) => {
        post('/Supplies/UpdateRequest', { id: request.id, dateOrdered: dateOrdered, isClosed: isClosed, adminNote: adminNote })
            .then(result => {
                if (handleOutcome(result, 'Update Request')) {
                    setRequestToClose(undefined);
                    getPayload();
                }
            });
    }

    const markOrdered = (request: SupplyRequest, dateOrdered: string, adminNote: string) => {
        updateRequest(request, dateOrdered, true, adminNote);
    }

    const closeWithoutOrdering = (request: SupplyRequest, adminNote: string) => {
        updateRequest(request, null, true, adminNote);
    }

    const reopenRequest = (request: SupplyRequest) => {
        updateRequest(request, null, false, request.adminNote);
    }

    const confirmWithdraw = (request: SupplyRequest) => {
        setRequestToWithdraw(request);
        showAlert('warning', 'Withdraw Request', `Withdraw your request "${request.requestText}"?`, undefined, 'Withdraw');
    }

    const withdrawRequest = (confirm: boolean) => {
        const request = requestToWithdraw;
        closeAlertInfoDialog();
        if (!confirm || !request)
            return;

        post('/Supplies/WithdrawRequest', { id: request.id })
            .then(result => {
                if (handleOutcome(result, 'Withdraw Request')) {
                    getPayload();
                }
            });
    }

    const formatDate = (date: string | null) => {
        return date ? dayjs(date).format('M/D/YYYY') : '';
    }

    const headerCell = (label: string) => {
        return <TableCell><Typography variant='body1' fontWeight={550}>{label}</Typography></TableCell>;
    }

    return (
        <div>
            <Grid container sx={{ display: 'flex' }}>
                <CssBaseline />
                <TopBar applicationName={applicationName}></TopBar>
            </Grid>
            <Grid item xs={12} marginBottom={1}>
                <Divider />
            </Grid>
            <Grid container sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', marginBottom: '15px' }} >
                <Grid item xs={10}>
                    {/* new request */}
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <TextField
                            label="What do you need?"
                            placeholder="e.g. 2 boxes of black pens, toner for the 2nd floor printer"
                            multiline
                            minRows={2}
                            size="small"
                            inputProps={{ maxLength: 1000 }}
                            value={newRequestText}
                            onChange={(e) => setNewRequestText(e.target.value)}
                            sx={{ flexGrow: 1, maxWidth: 700 }}
                        />
                        <Button variant="contained" disabled={isSaving || newRequestText.trim() === ''} onClick={addRequest}>
                            Request
                        </Button>
                    </Stack>

                    {/* open requests */}
                    <Typography variant="h6" fontWeight={400} marginTop={5} marginBottom={1}>
                        Open requests: {openRequests.length}
                    </Typography>
                    {
                        openRequests.length === 0 ?
                            <Typography variant="body2" color="text.secondary">Nothing waiting to be ordered.</Typography>
                            :
                            <TableContainer>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {headerCell('Requested')}
                                            {headerCell('By')}
                                            {headerCell('Request')}
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {openRequests.map((request: SupplyRequest) =>
                                            <TableRow key={request.id}>
                                                <TableCell width="10%"><Typography variant="body2">{formatDate(request.requestDate)}</Typography></TableCell>
                                                <TableCell width="15%"><Typography variant="body2">{request.requesterName}</Typography></TableCell>
                                                <TableCell><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{request.requestText}</Typography></TableCell>
                                                <TableCell width="20%" align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        {
                                                            request.requesterId === employeeId &&
                                                            <Button size="small" color="warning" onClick={() => confirmWithdraw(request)}>Withdraw</Button>
                                                        }
                                                        {
                                                            isSupplyAdmin &&
                                                            <Button size="small" variant="outlined" onClick={() => setRequestToClose(request)}>Ordered / Close</Button>
                                                        }
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                    }

                    {/* recently closed */}
                    {
                        closedRequests.length > 0 &&
                        <Typography variant="h6" fontWeight={400} marginTop={5} marginBottom={1}>Closed</Typography>
                    }
                    {
                        closedRequests.length > 0 &&
                        <TableContainer>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {headerCell('Requested')}
                                        {headerCell('By')}
                                        {headerCell('Request')}
                                        {headerCell('Status')}
                                        {headerCell('Note')}
                                        {isSupplyAdmin && <TableCell></TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {closedRequests.map((request: SupplyRequest) =>
                                        <TableRow key={request.id}>
                                            <TableCell width="10%"><Typography variant="body2">{formatDate(request.requestDate)}</Typography></TableCell>
                                            <TableCell width="15%"><Typography variant="body2">{request.requesterName}</Typography></TableCell>
                                            <TableCell><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{request.requestText}</Typography></TableCell>
                                            <TableCell width="15%">
                                                {
                                                    request.dateOrdered ?
                                                        <Chip size="small" color="success" variant="outlined" label={`Ordered ${formatDate(request.dateOrdered)}`} />
                                                        :
                                                        <Chip size="small" variant="outlined" label="Not ordered" />
                                                }
                                                <Typography variant="caption" display="block" color="text.secondary">{request.closedByName}</Typography>
                                            </TableCell>
                                            <TableCell width="20%"><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{request.adminNote}</Typography></TableCell>
                                            {
                                                isSupplyAdmin &&
                                                <TableCell width="8%" align="right">
                                                    <Button size="small" onClick={() => reopenRequest(request)}>Reopen</Button>
                                                </TableCell>
                                            }
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </Grid>
            </Grid>
            <CloseRequestDialog
                request={requestToClose}
                closeDialog={() => setRequestToClose(undefined)}
                markOrdered={markOrdered}
                closeWithoutOrdering={closeWithoutOrdering}
            ></CloseRequestDialog>
            {
                alertInfo &&
                <AlertInfoDialog
                    isAlertInfoDialogOpen={isAlertInfoDialogOpen}
                    alertInfo={alertInfo}
                    closeAlertInfoDialog={closeAlertInfoDialog}
                    confirmAction={withdrawRequest}
                ></AlertInfoDialog>
            }
        </div>
    );
};

export default SuppliesApp;
